import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { LastUpdated } from "../components/LastUpdated";
import { Search, Star, GitBranch, Copy, Download, Cpu } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Semua", "LLM Runner", "Framework", "Autonomous Agent", "MCP"];

export default function Repositories() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("search") || "");
  const cat = params.get("category") || "Semua";

  useEffect(() => {
    setLoading(true);
    const qp = new URLSearchParams();
    if (cat && cat !== "Semua") qp.append("category", cat);
    const s = params.get("search");
    if (s) qp.append("search", s);
    api.get(`/repositories?${qp.toString()}`).then((r) => {
      setItems(r.data);
      setLoading(false);
    });
  }, [params, cat]);

  const copy = (text, e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    toast.success("Perintah disalin");
  };

  const submit = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (q) p.set("search", q); else p.delete("search");
    setParams(p);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
      <div className="mb-8">
        <div className="chip chip-magenta mb-3"><Cpu className="w-3 h-3" /> Agent Catalog</div>
        <h1 className="font-heading text-4xl md:text-5xl font-black">Repository AI Agent</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">Kurasi repository open-source: Llama, Claude, Ollama, LangChain, AutoGPT, dan lainnya. Salin install command atau unduh langsung.</p>
        <LastUpdated className="mt-3" />
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <form onSubmit={submit} className="flex-1 flex items-center bg-card border border-white/10 rounded-full h-12 px-4 focus-within:border-magenta1/50">
          <Search className="w-4 h-4 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama repo, deskripsi, bahasa..."
            className="bg-transparent flex-1 outline-none px-3 text-sm placeholder:text-slate-600"
            data-testid="repos-search-input"
          />
          <button className="text-magenta1 text-sm font-medium" data-testid="repos-search-submit">Cari</button>
        </form>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => {
            const active = cat === c;
            return (
              <button
                key={c}
                onClick={() => {
                  const p = new URLSearchParams(params);
                  if (c === "Semua") p.delete("category"); else p.set("category", c);
                  setParams(p);
                }}
                className={`px-4 h-12 rounded-full border transition text-sm font-medium ${
                  active ? "border-magenta1 text-void bg-magenta1" : "border-white/10 text-slate-300 hover:border-magenta1/50 hover:text-magenta1"
                }`}
                data-testid={`filter-repo-${c}`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 text-center py-20">Memuat repository...</div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-center py-20" data-testid="repos-empty">Tidak ada repository yang cocok.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((r) => (
            <div key={r.id} className="card p-6 flex flex-col group" data-testid={`repo-${r.id}`}>
              <div className="flex items-start justify-between mb-3">
                <Link to={`/repositories/${r.id}`} className="min-w-0">
                  <div className="font-heading text-lg font-bold group-hover:text-cyan1 transition truncate">{r.name}</div>
                  <div className="font-mono text-[10px] text-slate-500 uppercase tracking-widest truncate">{r.full_name}</div>
                </Link>
                <span className="chip chip-green shrink-0"><Star className="w-3 h-3" /> {r.stars?.toLocaleString() || 0}</span>
              </div>
              <p className="text-sm text-slate-400 line-clamp-3 flex-1">{r.description}</p>
              <div className="mt-4 flex gap-2 flex-wrap">
                <span className="chip">{r.category}</span>
                {r.language && <span className="chip chip-cyan">{r.language}</span>}
              </div>
              {r.install_command && (
                <div className="terminal mt-4 text-xs">
                  <span className="truncate">{r.install_command}</span>
                  <button onClick={(e) => copy(r.install_command, e)} className="text-cyan1 hover:text-magenta1 shrink-0" data-testid={`copy-cmd-${r.id}`}>
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="mt-4 flex gap-2">
                <a href={r.github_url} target="_blank" rel="noreferrer" className="btn-ghost flex-1 justify-center !py-2 text-xs" data-testid={`github-${r.id}`}>
                  <GitBranch className="w-3.5 h-3.5" /> GitHub
                </a>
                {r.download_url && (
                  <a href={r.download_url} target="_blank" rel="noreferrer" className="btn-primary !py-2 !px-4 text-xs" data-testid={`download-${r.id}`}>
                    <Download className="w-3.5 h-3.5" /> Unduh
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
